import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Settings, Building2, Users, MapPin, Layers, Inbox, Truck } from 'lucide-react';
import { PageShell } from '@/components/primitives/PageShell';
import { PageHeader } from '@/components/primitives/PageHeader';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/contexts/AuthContext';
import { useTenant } from '@/contexts/TenantContext';
import { listPendingWildfires } from '@/api/wildfires';
import AdminInbox from './AdminInbox';
// Legacy admin pages — embed inside the hub until each gets its own rewrite (see c12 plan).
// eslint-disable-next-line @typescript-eslint/no-explicit-any
import SettingsPageRaw from './Settings';
// eslint-disable-next-line @typescript-eslint/no-explicit-any
import TenantListRaw from './TenantList';
// eslint-disable-next-line @typescript-eslint/no-explicit-any
import UserManagementRaw from './UserManagement';
// eslint-disable-next-line @typescript-eslint/no-explicit-any
import RoleManagementRaw from './RoleManagement';
// eslint-disable-next-line @typescript-eslint/no-explicit-any
import SuburbListRaw from './SuburbList';
// eslint-disable-next-line @typescript-eslint/no-explicit-any
import ParcelPipelineRaw from '@/pages/fpa/ParcelPipeline';
// eslint-disable-next-line @typescript-eslint/no-explicit-any
import OrganisationListRaw from './OrganisationList';
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const SettingsPage = SettingsPageRaw as any;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const TenantList = TenantListRaw as any;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const UserManagement = UserManagementRaw as any;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const RoleManagement = RoleManagementRaw as any;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const SuburbList = SuburbListRaw as any;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ParcelPipeline = ParcelPipelineRaw as any;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const OrganisationList = OrganisationListRaw as any;

type TabId =
  | 'inbox'
  | 'system'
  | 'fpa-areas'
  | 'organisations'
  | 'users'
  | 'roles'
  | 'suburbs'
  | 'parcels';
type Tab = { id: TabId; label: string; icon: typeof Settings };

export default function AdminHub() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { can, isAdmin, isSuperAdmin } = useAuth();
  const { tenantId } = useTenant();
  const canApprove = can('wildfire', 'approve');
  const [pendingCount, setPendingCount] = useState(0);

  // Each tab is shown only when the user has the backing capability, so an
  // FPA_ADMIN landing here sees just Users (+ Inbox) rather than the full
  // global-admin surface.
  const adminLike = isAdmin() || isSuperAdmin();
  const tabs: Tab[] = [
    canApprove && { id: 'inbox', label: 'Inbox', icon: Inbox },
    can('system', 'read') && { id: 'system', label: 'System', icon: Settings },
    can('tenant', 'list') && { id: 'fpa-areas', label: 'FPA areas', icon: Building2 },
    // Writes are super-admin only, so the tab keys off organisation_create
    // rather than _list: everyone can read the register, but only a super
    // admin has anything to do on this screen.
    can('organisation', 'create') && {
      id: 'organisations',
      label: 'Organisations',
      icon: Truck,
    },
    can('user', 'list') && { id: 'users', label: 'Users', icon: Users },
    can('user', 'manage') && { id: 'roles', label: 'Roles', icon: Users },
    can('suburb', 'create') && { id: 'suburbs', label: 'Suburbs', icon: MapPin },
    adminLike && { id: 'parcels', label: 'Parcels', icon: Layers },
  ].filter(Boolean) as Tab[];

  // Pre-fetch the pending count so the tab can show a badge before it's opened.
  useEffect(() => {
    if (!canApprove) return;
    let cancelled = false;
    listPendingWildfires(tenantId ? { tenant_id: tenantId } : {})
      .then((d: { wildfires?: unknown[] }) => {
        if (!cancelled) setPendingCount((d.wildfires || []).length);
      })
      .catch(() => {/* ignore */});
    return () => {
      cancelled = true;
    };
  }, [canApprove, tenantId]);

  const requested = searchParams.get('tab') as TabId | null;
  const tab: TabId =
    (requested && tabs.some((t) => t.id === requested) ? requested : tabs[0]?.id) ?? 'users';

  const setTab = (next: string) => {
    const sp = new URLSearchParams(searchParams);
    sp.set('tab', next);
    setSearchParams(sp, { replace: true });
  };

  return (
    <PageShell>
      <PageHeader
        title="Admin settings"
        subtitle="System configuration & data"
      />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-4 flex h-auto flex-wrap gap-1">
          {tabs.map((t) => {
            const Icon = t.icon;
            return (
              <TabsTrigger key={t.id} value={t.id} className="gap-2">
                <Icon className="h-4 w-4" />
                {t.label}
                {t.id === 'inbox' && pendingCount > 0 && (
                  <Badge variant="secondary" className="ml-1">{pendingCount}</Badge>
                )}
              </TabsTrigger>
            );
          })}
        </TabsList>

        {canApprove && (
          <TabsContent value="inbox" className="mt-0">
            <AdminInbox onCountChange={setPendingCount} />
          </TabsContent>
        )}

        <TabsContent value="system" className="mt-0">
          <SettingsPage embedded />
        </TabsContent>
        <TabsContent value="fpa-areas" className="mt-0">
          <TenantList embedded />
        </TabsContent>
        <TabsContent value="organisations" className="mt-0">
          <OrganisationList />
        </TabsContent>
        <TabsContent value="users" className="mt-0">
          <UserManagement embedded />
        </TabsContent>
        <TabsContent value="roles" className="mt-0">
          <RoleManagement embedded />
        </TabsContent>
        <TabsContent value="suburbs" className="mt-0">
          <SuburbList embedded />
        </TabsContent>
        <TabsContent value="parcels" className="mt-0">
          <ParcelPipeline embedded />
        </TabsContent>
      </Tabs>
    </PageShell>
  );
}
